import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { ExpenseSubCategory } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Expense Sub Categories',
    description: 'Manage expense sub categories',
    permissionModuleKey: 'expense_subcategories',
    route: {
      list: '/p_crm_expense_subcategory',
      new: '/p_crm_expense_subcategory/new',
      detail: (id: number) => `/p_crm_expense_subcategory/${id}`,
      edit: (id: number) => `/p_crm_expense_subcategory/${id}/edit`,
    },
    api: {
      list: '/crm-expense-sub-category',
      detail: (id: number) => `/crm-expense-sub-category/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search expense sub categories...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['name', 'exp_category_id'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Type to filter name...',
        type: 'text' as const,
        getValue: (item: ExpenseSubCategory) => item.name,
        options: [],
      },
      {
        key: 'exp_category_id',
        label: 'Category',
        placeholder: 'All Categories',
        type: 'select' as const,
        getValue: (item: ExpenseSubCategory) => 
          item.exp_category_desc?.name || (item.exp_category_id ? `Category #${item.exp_category_id}` : ''),
        options: [],
      },
    ],
    applyFilter: (items: ExpenseSubCategory[], filters: { name: string[] | string; exp_category_id: string[] }) => {
      return items.filter((item) => {
        const nameFilter = filters.name;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          const value = (item.name || '').toLowerCase();
          if (!value.includes(nameFilter.toLowerCase())) return false;
        } else if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          const value = (item.name || '').toLowerCase();
          const matches = nameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        
        // Category filter (matches by display name or ID)
        if (filters.exp_category_id.length > 0) {
          const displayValue = item.exp_category_desc?.name || (item.exp_category_id ? `Category #${item.exp_category_id}` : '');
          const matches = filters.exp_category_id.some((filter) => {
            return displayValue === filter || 
                   (item.exp_category_id && filter === `Category #${item.exp_category_id}`) ||
                   (item.exp_category_desc?.name && filter === item.exp_category_desc.name);
          });
          if (!matches) return false;
        }
        
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Expense Sub Category',
        showTitle: false,
        variant: 'primary' as const,
        href: '/p_crm_expense_subcategory/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: ExpenseSubCategory) => {
      window.location.href = `/p_crm_expense_subcategory/${item.id}`;
    },
    keyExtractor: (item: ExpenseSubCategory) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new expense sub category.',
    noSearchResults: 'No expense sub categories found matching your search criteria.',
    noFilterResults: 'No expense sub categories match the selected filters.',
  },
  delete: {
    title: 'Delete Expense Sub Category',
    message: 'Are you sure you want to delete this expense sub category? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete expense sub category. Please try again.',
    handler: async (id: number) => {
      const { deleteExpenseSubCategory } = await import('../../../services/mutations');
      await deleteExpenseSubCategory(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Expense Sub Category Details',
    notFoundTitle: 'Expense Sub Category not found',
    notFoundMessage: 'The expense sub category you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Expense Sub Categories',
    defaultTitle: 'Unnamed Expense Sub Category',
    getTitle: (item: ExpenseSubCategory) => {
      return item.name || `Expense Sub Category #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Expense Sub Category',
      subtitle: 'Add a new expense sub category',
      submitText: 'Create Expense Sub Category',
    },
    edit: {
      title: 'Edit Expense Sub Category',
      subtitle: 'Update expense sub category information',
      submitText: 'Update Expense Sub Category',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading expense sub category...',
    errorMessage: 'Failed to save expense sub category. Please try again.',
    create: async (data: Partial<ExpenseSubCategory>) => {
      const { createExpenseSubCategory } = await import('../../../services/mutations');
      return await createExpenseSubCategory(data);
    },
    update: async (id: number, data: Partial<ExpenseSubCategory>) => {
      const { updateExpenseSubCategory } = await import('../../../services/mutations');
      return await updateExpenseSubCategory(id, data);
    },
  },
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter expense sub category name',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
    {
      name: 'exp_category_id',
      label: 'Category',
      type: 'select',
      placeholder: 'Select a category (optional)',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/crm-expense-category', // API endpoint to fetch all expense category options
      labelField: 'name', // Field name in API response to use as label
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Expense Sub Category Information',
        icon: '📝',
        fields: ['name', 'exp_category_id'],
        columns: 2,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<ExpenseSubCategory>[] => [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (value) => (
      value || '-'
    ),
  },
  {
    key: 'exp_category_desc',
    header: 'Category',
    sortable: true,
    render: (value, row) => (
      row.exp_category_desc?.name || '-'
    ),
  },
];

export const getDetailSections = (
  item: ExpenseSubCategory,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Name',
          value: item.name || '-',
        },
        {
          label: 'Category',
          value: item.exp_category_desc?.name || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: ExpenseSubCategory) => {
  return null;
};

export const getInitialFormData = (): Partial<ExpenseSubCategory> => {
  return {
    name: '',
    exp_category_id: undefined,
  };
};

export const mapItemToFormData = (item: ExpenseSubCategory): Partial<ExpenseSubCategory> => {
  return {
    name: item.name || '',
    exp_category_id: item.exp_category_id,
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: ExpenseSubCategory,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

